import { JobApplication } from "../Entities/JobApplication";
import {
  CandidateJobApplicationFilters,
  FindAllJobApplicationFilters,
  JobApplicationFilters,
  PaginationType,
} from "../Interface";
import apiClient from "./api.client.service";
import AuthService from "./auth.service";
import { UserApplicationsResponse } from "../Pages/ApplicationDetails/components/ApplicationListing/ApplicationListing.types";

export interface BulkStatusChangeResult {
  changed: number;
  skipped: number;
  failed: number;
  failures: Array<{ id: string; reason: string }>;
}

class JobApplicationService {
  private APIBaseUrl: string;
  private allApplicationsCache: JobApplication[] = [];

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
    AuthService.getInstance().subscribe(
      AuthService.EVENTS.USER_SIGNED_OUT,
      () => {
        this.clearJobApplicationData();
      },
    );
  }

  async getAllJobApplications(): Promise<any[]> {
    if (this.allApplicationsCache.length > 0) return this.allApplicationsCache;
    try {
      const response = await apiClient.get<any>(
        `${this.APIBaseUrl}/admin/job-application`,
      );
      if (response.data) {
        this.allApplicationsCache = response?.data?.data;
        return response.data.data;
      } else {
        return [];
      }
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ getAllJobApplications ~ error:",
        error,
      );
      throw error;
    }
  }

  async getJobApplicationById(id: string): Promise<JobApplication | undefined> {
    if (!this.allApplicationsCache.length) {
      await this.getAllJobApplications();
    }
    const application = this.allApplicationsCache.find(
      (application) => application._id === id,
    );
    if (application) {
      return application;
    }

    try {
      const response = await apiClient.get<{ data: JobApplication[] }>(
        `${this.APIBaseUrl}/admin/job-application?jobApplicationId=${id}`,
      );

      return response.data.data[0];
    } catch (error) {
      console.error(
        "JobApplicationService.getJobApplicationById() failed with error:",
        error,
      );
    }
  }

  async updateJobApplication(applicationId: string, status: string) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/job-application/${applicationId}/${status}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ updateJobApplication ~ error:",
        error,
      );
    }
  }

  async bulkUpdateStatus(
    jobApplicationIds: string[],
    status: string,
  ): Promise<BulkStatusChangeResult> {
    const response = await apiClient.post<BulkStatusChangeResult>(
      `${this.APIBaseUrl}/admin/job-application/bulk-status`,
      { jobApplicationIds, status },
    );
    return response.data;
  }

  async getJobApplicationByStatusAndJobDescriptionId(
    status: string,
    jobDescriptionId: string,
    page: number,
    limit: number,
  ) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-application?status=${status}&jobDescriptionId=${jobDescriptionId}&page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ getJobApplicationByStatusAndJobDescriptionId ~ error:",
        error,
      );
    }
  }

  async getAllJobApplicationsByuserId(
    userId: string,
    page: number,
    limit: number,
    filter?: string,
    sort?: string,
  ): Promise<UserApplicationsResponse> {
    try {
      const params = new URLSearchParams();
      params.append("userId", userId);
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      // Always send filter parameter (even if "all") to ensure backend returns counts
      if (filter) {
        params.append("filter", filter);
      } else {
        params.append("filter", "all");
      }

      if (sort) {
        params.append("sort", sort);
      }

      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-application?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ getAllJobApplicationsByuserId ~ error:",
        error,
      );
      // Return default response on error
      return {
        data: [],
        total: 0,
        currentPage: 1,
        totalPages: 0,
        counts: {
          all: 0,
          profileQualified: 0,
          profileUnqualified: 0,
          profilePending: 0,
          aiQualified: 0,
          aiUnqualified: 0,
          aiPending: 0,
          progressed: 0,
          shortlisted: 0,
          interview: 0,
          hired: 0,
          rejected: 0,
        },
      };
    }
  }

  clearJobApplicationData() {
    this.allApplicationsCache = [];
  }

  async moveToAIinterview(applicationId: string) {
    try {
      const response = await apiClient.put(
        `${this.APIBaseUrl}/admin/job-application/${applicationId}/move-to-ai-interview`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ moveToAIinterview ~ error:",
        error,
      );
    }
  }

  async loadAvailableInterviewRounds() {
    try {
      const response = await fetch("/data/interview-rounds.json");
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ loadInterviewRounds ~ error:",
        error,
      );
      return [];
    }
  }

  async findAllJobApplicationWithFilters(
    jobDescriptionId: string,
    payload: FindAllJobApplicationFilters,
    status: string | null,
    page: number | null,
    limit: number | null,
  ): Promise<any> {
    const { search, interviewStatus, sort, scheduleDate } = payload;

    try {
      const query = new URLSearchParams();

      if (page !== null && limit !== null) {
        query.append("page", page.toString());
        query.append("limit", limit.toString());
      }
      if (search) query.append("search", search);
      if (status) query.append("status", status);
      if (interviewStatus) query.append("interviewStatus", interviewStatus);
      if (sort) query.append("sort", sort);
      if (scheduleDate)
        query.append("scheduleDate", scheduleDate.toISOString());

      const paramString = query.toString() ? `?${query.toString()}` : "";
      const url = `/admin/job-application/${jobDescriptionId}/all${paramString}`;

      console.log("🚀 ~ JobApplicationService ~ url:", url);

      const response = await apiClient.get(`${this.APIBaseUrl}${url}`);
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ JobApplicationService ~ findAllJobApplicationWithFilters ~ error:",
        error,
      );
      return [];
    }
  }

  async getJobApplicationStatistics(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<
    {
      date: string;
      applied: number;
      qualified: number;
      shortlisted: number;
      hired: number;
      rejected: number;
    }[]
  > {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/statistics/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);

      // Handle both response.data.data and direct response.data
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(
        "🚀 ~ JobApplicationService ~ getJobApplicationStatistics ~ error:",
        error,
      );
      return [];
    }
  }

  async findAllJobApplications(
    jobDescriptionId: string,
    pagination: PaginationType,
    filters: JobApplicationFilters,
  ) {
    const { page, limit } = pagination;
    const {
      interviewStatus,
      profileMatchSatus,
      roundStatus,
      search,
      sort,
      status,
      uploadType,
      requirementId,
      requirementMatch,
    } = filters;
    try {
      let params = `?page=${page}&limit=${limit}`;
      if (status) {
        params += `&status=${status}`;
      }
      if (interviewStatus) {
        params += `&interviewStatus=${interviewStatus}`;
      }
      if (profileMatchSatus) {
        params += `&profileMatchSatus=${profileMatchSatus}`;
      }
      if (roundStatus) {
        params += `&roundStatus=${roundStatus}`;
      }
      if (search) {
        params += `&search=${search}`;
      }
      if (sort) {
        params += `&sort=${sort}`;
      }
      if (uploadType) {
        params += `&uploadType=${uploadType}`;
      }
      if (requirementId) {
        params += `&requirementId=${requirementId}`;
      }
      if (requirementMatch) {
        params += `&requirementMatch=${requirementMatch}`;
      }
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-application/${jobDescriptionId}/all/${params}`,
      );
      return response.data;
    } catch (error: any) {
      console.log(
        "🚀 ~ JobApplicationService ~ findAllJobApplications ~ error:",
        error,
      );
      return {
        jobApplications: [],
        totalPages: 0,
        totalRecords: 0,
      };
    }
  }

  // Analytics Methods
  async getApplicationFunnel(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<{
    applied: number;
    resumeUploaded: number;
    qualified: number;
    assessmentCompleted: number;
    shortlisted: number;
    interview: number;
    hired: number;
    rejected: number;
    dropOffRates: {
      resumeUpload: number;
      profileMatch: number;
      assessment: number;
      shortlist: number;
      interview: number;
      hire: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/funnel/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          applied: 0,
          resumeUploaded: 0,
          qualified: 0,
          assessmentCompleted: 0,
          shortlisted: 0,
          interview: 0,
          hired: 0,
          rejected: 0,
          dropOffRates: {
            resumeUpload: 0,
            profileMatch: 0,
            assessment: 0,
            shortlist: 0,
            interview: 0,
            hire: 0,
          },
        }
      );
    } catch (error) {
      console.error("Error fetching application funnel:", error);
      throw error;
    }
  }

  async getAssessmentCompletionRate(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<{
    totalApplied: number;
    assessmentCompleted: number;
    assessmentPending: number;
    completionRate: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/assessment-completion/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          totalApplied: 0,
          assessmentCompleted: 0,
          assessmentPending: 0,
          completionRate: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching assessment completion rate:", error);
      throw error;
    }
  }

  async getQualificationMetrics(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<{
    totalApplied: number;
    qualified: number;
    unqualified: number;
    pending: number;
    qualificationRate: number;
    averageMatchScore: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/qualification/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          totalApplied: 0,
          qualified: 0,
          unqualified: 0,
          pending: 0,
          qualificationRate: 0,
          averageMatchScore: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching qualification metrics:", error);
      throw error;
    }
  }

  async getTimeToQualification(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?: "day" | "week" | "month" | "custom",
  ): Promise<{
    averageDays: number;
    medianDays: number;
    minDays: number;
    maxDays: number;
    dataPoints: Array<{ applicationId: string; days: number }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/time-to-qualification/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          averageDays: 0,
          medianDays: 0,
          minDays: 0,
          maxDays: 0,
          dataPoints: [],
        }
      );
    } catch (error) {
      console.error("Error fetching time to qualification:", error);
      throw error;
    }
  }

  async getInterviewRoundSuccessRates(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<{
    summary: {
      candidatesStarted: number;
      candidatesQualified: number;
      candidatesUnqualified: number;
      candidatesPending: number;
      manuallyAdvancedCount: number;
    };
    rounds: Array<{
      roundNumber: number;
      roundId: string;
      roundName: string;
      totalStarted: number;
      qualified: number;
      unqualified: number;
      pending: number;
      successRate: number;
    }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/interview-rounds/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          summary: {
            candidatesStarted: 0,
            candidatesQualified: 0,
            candidatesUnqualified: 0,
            candidatesPending: 0,
            manuallyAdvancedCount: 0,
          },
          rounds: [],
        }
      );
    } catch (error) {
      console.error("Error fetching interview round success rates:", error);
      throw error;
    }
  }

  async getDropOffAnalysis(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?:
      | "today"
      | "last7days"
      | "last30days"
      | "last90days"
      | "day"
      | "week"
      | "month"
      | "allTime"
      | "custom",
  ): Promise<{
    stages: Array<{
      stage: string;
      entered: number;
      dropped: number;
      dropOffRate: number;
    }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/drop-off/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return response.data?.data || { stages: [] };
    } catch (error) {
      console.error("Error fetching drop-off analysis:", error);
      throw error;
    }
  }

  async getMatchScoreDistribution(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?: "day" | "week" | "month" | "custom",
  ): Promise<{
    distribution: Array<{
      range: string;
      count: number;
      percentage: number;
    }>;
    average: number;
    median: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/match-score-distribution/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          distribution: [],
          average: 0,
          median: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching match score distribution:", error);
      throw error;
    }
  }

  async getSourceEffectiveness(
    jobPostId: string,
    startDate?: string,
    endDate?: string,
    filterType?: "day" | "week" | "month" | "custom",
  ): Promise<{
    sources: Array<{
      source: string;
      total: number;
      qualified: number;
      qualificationRate: number;
    }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (filterType) params.append("filterType", filterType);

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/source-effectiveness/${jobPostId}${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get(url);
      return response.data?.data || { sources: [] };
    } catch (error) {
      console.error("Error fetching source effectiveness:", error);
      throw error;
    }
  }

  async getApplicationStatistics(
    jobPostId: string,
    filters?: JobApplicationFilters,
  ): Promise<{
    totalApplied: number;
    profileMatch: {
      qualified: number;
      unqualified: number;
      pending: number;
    };
    profileMatchPercent: {
      qualified: number;
      unqualified: number;
      pending: number;
    };
    aiInterviews: {
      qualified: number;
      unqualified: number;
      pending: number;
    };
    aiInterviewsPercent: {
      qualified: number;
      unqualified: number;
      pending: number;
    };
    shortlisted: number;
    shortlistedPercent: number;
    rejected: number;
    rejectedPercent: number;
    hired: number;
    hiredPercent: number;
    interview: number;
    interviewPercent: number;
    interviewScheduled: number;
    interviewScheduledPercent: number;
  }> {
    try {
      const params = new URLSearchParams();

      // Add filter parameters using exact JobApplicationFilters interface
      // status: JobApplicationStatus | "qualified" | null
      if (filters?.status) {
        params.append("status", filters.status);
      }

      // profileMatchSatus: "qualified" | "unqualified" | "NA" | null
      if (filters?.profileMatchSatus) {
        params.append("profileMatchSatus", filters.profileMatchSatus);
      }

      // roundStatus: "pending" | "qualified" | "unqualified" | null
      if (filters?.roundStatus) {
        params.append("roundStatus", filters.roundStatus);
      }

      // interviewStatus: "scheduled" | "not_scheduled" | null
      if (filters?.interviewStatus) {
        params.append("interviewStatus", filters.interviewStatus);
      }

      // uploadType: "company" | "candidate" | null
      if (filters?.uploadType) {
        params.append("uploadType", filters.uploadType);
      }

      // search: string | null
      if (filters?.search && filters.search.trim() !== "") {
        params.append("search", filters.search.trim());
      }

      // requirementId: number | null
      if (filters?.requirementId) {
        params.append("requirementId", String(filters.requirementId));
      }

      // requirementMatch: "matched" | "unmatched" | null
      if (filters?.requirementMatch) {
        params.append("requirementMatch", filters.requirementMatch);
      }

      const queryString = params.toString();
      const url = `${this.APIBaseUrl}/job-description/analytics/statistics/${jobPostId}${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get(url);
      const data = response.data?.data;

      // Ensure all required fields exist with defaults
      return {
        totalApplied: data?.totalApplied || 0,
        profileMatch: {
          qualified: data?.profileMatch?.qualified || 0,
          unqualified: data?.profileMatch?.unqualified || 0,
          pending: data?.profileMatch?.pending || 0,
        },
        profileMatchPercent: {
          qualified: data?.profileMatchPercent?.qualified || 0,
          unqualified: data?.profileMatchPercent?.unqualified || 0,
          pending: data?.profileMatchPercent?.pending || 0,
        },
        aiInterviews: {
          qualified: data?.aiInterviews?.qualified || 0,
          unqualified: data?.aiInterviews?.unqualified || 0,
          pending: data?.aiInterviews?.pending || 0,
        },
        aiInterviewsPercent: {
          qualified: data?.aiInterviewsPercent?.qualified || 0,
          unqualified: data?.aiInterviewsPercent?.unqualified || 0,
          pending: data?.aiInterviewsPercent?.pending || 0,
        },
        shortlisted: data?.shortlisted || 0,
        shortlistedPercent: data?.shortlistedPercent || 0,
        rejected: data?.rejected || 0,
        rejectedPercent: data?.rejectedPercent || 0,
        hired: data?.hired || 0,
        hiredPercent: data?.hiredPercent || 0,
        interview: data?.interview || 0,
        interviewPercent: data?.interviewPercent || 0,
        interviewScheduled: data?.interviewScheduled || 0,
        interviewScheduledPercent: data?.interviewScheduledPercent || 0,
      };
    } catch (error: any) {
      console.error("Failed to fetch application statistics:", error);
      throw error;
    }
  }

  async getRequirementsInsights(jobPostId: string): Promise<{
    summary: {
      totalRequirements: number;
      mustMatchCount: number;
      optionalCount: number;
      // Candidate-focused metrics
      totalApplications: number;
      totalEvaluated: number;
      pendingCount: number;
      qualifiedCount: number;
      failedCount: number;
    };
    requirementStats: Array<{
      requirementId: number;
      description: string;
      topic: string;
      isMustMatch: boolean;
      stats: {
        matched: number;
        unmatched: number;
        partial: number;
        total: number;
        matchRate: number;
      };
    }>;
  }> {
    try {
      const url = `${this.APIBaseUrl}/job-description/analytics/requirements-insights/${jobPostId}`;
      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          summary: {
            totalRequirements: 0,
            mustMatchCount: 0,
            optionalCount: 0,
            totalApplications: 0,
          },
          requirementStats: [],
        }
      );
    } catch (error: any) {
      console.error("Failed to fetch requirements insights:", error);
      throw error;
    }
  }

  async getInterviewerWorkload(jobPostId: string): Promise<{
    summary: {
      totalInterviewers: number;
      totalUpcoming: number;
      totalCompleted: number;
      totalPendingSlot: number;
    };
    interviewers: Array<{
      id: string;
      name: string;
      email: string;
      stats: {
        upcoming: number;
        completed: number;
        total: number;
      };
    }>;
  }> {
    try {
      const url = `${this.APIBaseUrl}/job-description/analytics/interviewer-workload/${jobPostId}`;
      const response = await apiClient.get(url);
      return (
        response.data?.data || {
          summary: {
            totalInterviewers: 0,
            totalUpcoming: 0,
            totalCompleted: 0,
            totalPendingSlot: 0,
          },
          interviewers: [],
        }
      );
    } catch (error: any) {
      console.error("Failed to fetch interviewer workload:", error);
      throw error;
    }
  }

  async downloadApplicationReport(
    applicationId: string,
    maskPersonalDetails: boolean,
  ) {
    try {
      const response = await apiClient.get(
        `/admin/job-application/report/${applicationId}`,
        {
          responseType: "blob",
          params: { maskPersonalDetails: maskPersonalDetails },
        },
      );
      return response || null;
    } catch (error: any) {
      console.error(
        "JobApplicationService ~ downloadApplicationReport ~ error:",
        error,
      );
      return null;
    }
  }

  async downloadBulkApplicationReport(
    applicationIds: string[],
    maskPersonalDetails: boolean,
  ) {
    try {
      const response = await apiClient.post(
        `/admin/job-application/report/bulk`,
        { applicationIds, maskPersonalDetails },
        { responseType: "blob" },
      );
      return (response.data as Blob) || null;
    } catch (error: any) {
      console.error(
        "JobApplicationService ~ downloadBulkApplicationReport ~ error:",
        error,
      );
      return null;
    }
  }

  async getAllCandidateApplications(
    candidateId: string,
    filters: CandidateJobApplicationFilters,
    page: number,
    limit: number,
  ) {
    try {
      let params = `?page=${page}&limit=${limit}`;
      const {
        search,
        sort,
        source,
        assessmentStatus,
        onlineInterviewStatus,
        status,
        profileMatchStatus,
      } = filters;
      if (search) {
        params += `&search=${search}`;
      }
      if (sort) {
        params += `&sort=${sort}`;
      }
      if (source) {
        params += `&source=${source}`;
      }
      if (assessmentStatus) {
        params += `&assessmentStatus=${assessmentStatus}`;
      }
      if (onlineInterviewStatus) {
        params += `&onlineInterviewStatus=${onlineInterviewStatus}`;
      }
      if (status) {
        params += `&status=${status}`;
      }
      if (profileMatchStatus) {
        params += `&profileMatchStatus=${profileMatchStatus}`;
      }

      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-application/candidate/${candidateId}${params}`,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "~ JobApplicationService ~ getAllCandidateApplications ~ error:",
        error,
      );
      return null;
    }
  }
}

const jobApplicationService = new JobApplicationService();
export default jobApplicationService;
